import React, { useMemo, useState } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { Box, IconButton, Tooltip, TextField, MenuItem, Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import axiosInstance from "../../Utils/axiosInstance";
import { useQuery } from "@tanstack/react-query";
import { Container, Row } from "reactstrap";
import Accordion from "react-bootstrap/Accordion";
import { toast } from "react-toastify";
import { showDeleteConfirmModal } from "../../Components/ConfirmModal";
import { CommonToolbarInternalActions } from "../../Components/SharedComponents/CommonTableToolbarActions";
import { GenericMappingTableProps, MappingOption } from "../../Types/GenericMapping.type";
import BrokerTemplateDownloadButton from "../../Components/TemplateDownloadButton/BrokerTemplateDownloadButton";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import FileUpload from "../../Components/FileUpload/FileUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";


const GenericMappingTable: React.FC<GenericMappingTableProps> = ({
  pageTitle,
  entityLabel,
  entityFieldName,
  selectLabel,
  selectFieldName,
  selectDisplayFieldName,
  dataApiUrl,
  optionsApiUrl,
  queryKeyData,
  queryKeyOptions,
  deleteConfirmLabel,
  updateApiUrl,
  bulkDeleteApiUrl,
  deleteMode = "rest",
  fileUploadApiUrl,
  exportAllApiUrl,
  exportAllFileName = "export.csv",
  templateDownloadApiUrl,
  templateFileName = "template.csv",
}) => {
  const [inputBackgroundColour, setInputBackgroundColour] = useState("");
  const [editId, setEditId] = useState<string | null>(null);
  const [originalEditData, setOriginalEditData] = useState<Record<string, string> | null>(null);
  const [openFileUpload, setOpenFileUpload] = useState(false);


  const [formData, setFormData] = useState<Record<string, string>>({
    [entityFieldName]: "",
    [selectFieldName]: "",
  });

  const [errors, setErrors] = useState<Record<string, boolean>>({
    [entityFieldName]: false,
    [selectFieldName]: false,
  });

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  const { data: selectOptions = [] } = useQuery<MappingOption[]>({
    queryKey: [queryKeyOptions],
    queryFn: async () => {
      const res = await axiosInstance.get(optionsApiUrl);
      return res.data;
    },
    refetchOnWindowFocus: false,
  });

  const { data = [], isLoading, refetch } = useQuery<any[]>({
    queryKey: [queryKeyData],
    queryFn: async () => {
      const res = await axiosInstance.get(dataApiUrl);
      return res.data;
    },
    refetchOnWindowFocus: false,
  });

  const handleFileUploadSubmit = async (file: File) => {
    if (!fileUploadApiUrl) return { success: false, message: "Upload not configured" };
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axiosInstance.post(fileUploadApiUrl, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      refetch();
      toast.success(response.data?.message || "File uploaded successfully!");
      return { success: true, message: response.data?.message, data: response.data };
    } catch (error: any) {
      const msg = error?.response?.data?.message || "Failed to upload file";
      toast.error(msg);
      return { success: false, message: msg };
    }
  };

  const handleExportSelectedRows = () => {
    const selectedIds = Object.keys(rowSelection);
    if (selectedIds.length === 0) {
      toast.warn("Please select at least one row to export");
      return;
    }
    try {
      const selectedData = data.filter((row: any) => rowSelection[row.id]);
      const headers = [entityFieldName, selectDisplayFieldName];
      const csv = [
        headers.join(","),
        ...selectedData.map((d: any) =>
          headers.map((h) => `"${String(d[h] ?? "").replace(/"/g, '""')}"`).join(",")
        ),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `selected_${queryKeyData}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Export started successfully");
    } catch (err) {
      toast.error("Failed to export selected rows");
    }
  };

  const handleDataDownload = async () => {
    if (!exportAllApiUrl) return;
    try {
      const response = await axiosInstance.get(exportAllApiUrl, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", exportAllFileName);
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      toast.success("Data downloaded successfully");
    } catch (err) {
      toast.error("Failed to download data");
    }
  };

  const compactInputStyle = {
    width: "250px",
    "& .MuiInputBase-root": { height: "32px", fontSize: "0.75rem", fontWeight: "bold" },
    "& .MuiInputLabel-root": {
      fontSize: "0.9rem",
      transform: "translate(14px, -8px) scale(0.74)",
      fontWeight: "bold",
      color: "black",
      paddingRight: "0.4rem",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#b9baeb85", borderWidth: "2px" },
    "& .MuiMenuItem-root": { fontSize: "0.75rem" },
  };

  const validate = () => {
    const newErrors = {
      [entityFieldName]: !formData[entityFieldName]?.trim(),
      [selectFieldName]: !formData[selectFieldName],
    };
    setErrors(newErrors);
    return !newErrors[entityFieldName] && !newErrors[selectFieldName];
  };

const columns = useMemo(
  () => [
    { accessorKey: entityFieldName, header: entityLabel, size: 400, minSize: 100, maxSize: 600, grow: false },
    { accessorKey: selectDisplayFieldName, header: selectLabel, size: 400, minSize: 100, maxSize: 600, grow: false },
  ],
  [entityFieldName, entityLabel, selectDisplayFieldName, selectLabel]
);

  const emptyForm = () => ({ [entityFieldName]: "", [selectFieldName]: "" });

  const isUnchangedInEditMode =
    !!editId &&
    !!originalEditData &&
    formData[entityFieldName] === originalEditData[entityFieldName] &&
    formData[selectFieldName] === originalEditData[selectFieldName];

  const handleCreate = async () => {
    if (!validate()) {
      return;
    }
    try {
      await axiosInstance.post(dataApiUrl, formData);
      setFormData(emptyForm());
      refetch();
      toast.success(`${entityLabel.replace(" Name", "")} added successfully!`);
    } catch (err: any) {
      const msg = err?.response?.data?.[Object.keys(err?.response?.data || {})[0]]?.[0];
      toast.error(msg || `Failed to add ${entityLabel.replace(" Name", "")}!`);
      console.error(err);
    }
  };

  const handleClear = () => {
    setFormData(emptyForm());
    setErrors({ [entityFieldName]: false, [selectFieldName]: false });
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setFormData(emptyForm());
    setOriginalEditData(null);
  };
  const handleUpdate = async () => {
    if (!editId) return;
    try {
      await axiosInstance.patch(`${updateApiUrl || dataApiUrl}${editId}/`, formData);
      toast.success(`${entityLabel.replace(" Name", "")} updated successfully!`);
      setEditId(null);
      setFormData(emptyForm());
      setOriginalEditData(null);
      setErrors({ [entityFieldName]: false, [selectFieldName]: false });
      refetch();
    } catch (err: any) {
      const msg = err?.response?.data?.[Object.keys(err?.response?.data || {})[0]]?.[0];
      toast.error(msg || `Failed to update ${entityLabel.replace(" Name", "")}!`);
      console.error(err);
    }
  };

  const fillFormForEdit = (row: any) => {
    setEditId(row.original.id);
    const initialData = {
      [entityFieldName]: row.original[entityFieldName],
      [selectFieldName]: row.original[selectFieldName],
    };
    setFormData(initialData);
    setOriginalEditData(initialData); // NEW
    setErrors({ [entityFieldName]: false, [selectFieldName]: false });
  };
  const handleDelete = (row: any) => {
    showDeleteConfirmModal({
      title: `Are you sure you want to delete this ${deleteConfirmLabel || entityLabel}?`,
      content: "Deleting...",
      onOk: async () => {
        try {
          let res;
          if (deleteMode === "bulk-post" && bulkDeleteApiUrl) {
            res = await axiosInstance.post(bulkDeleteApiUrl, { ids: [row.original.id] });
          } else if (deleteMode === "bulk-delete" && bulkDeleteApiUrl) {
            res = await axiosInstance.delete(bulkDeleteApiUrl, { data: { ids: [row.original.id] } });
          } else {
            res = await axiosInstance.delete(`${dataApiUrl}${row.original.id}/`);
          }
          toast.success(res?.data?.message || "Deleted successfully!");
          refetch();
        } catch (err: any) {
          toast.error(err?.response?.data?.message || "Failed to delete!");
          console.error(err);
        }
      },
    });
  };

  const handleBulkDelete = () => {
    const selectedIds = Object.keys(rowSelection);
    if (selectedIds.length === 0) return;

    showDeleteConfirmModal({
      title: `Are you sure you want to delete ${selectedIds.length} selected record(s)?`,
      content: "Deleting...",
      onOk: async () => {
        try {
          let res;
          if (deleteMode === "bulk-post" && bulkDeleteApiUrl) {
            res = await axiosInstance.post(bulkDeleteApiUrl, { ids: selectedIds });
          } else if (deleteMode === "bulk-delete" && bulkDeleteApiUrl) {
            res = await axiosInstance.delete(bulkDeleteApiUrl, { data: { ids: selectedIds } });
          } else {
            await Promise.all(selectedIds.map((id) => axiosInstance.delete(`${dataApiUrl}${id}/`)));
          }
          toast.success(res?.data?.message || `${selectedIds.length} record(s) deleted successfully!`);
          setRowSelection({});
          refetch();
        } catch (err: any) {
          toast.error(err?.response?.data?.message || "Failed to delete selected records!");
          console.error(err);
        }
      },
    });
  };
  const handleCopyRow = (row: any) => {
    setEditId(null);
    setFormData({
      [entityFieldName]: row.original[entityFieldName],
      [selectFieldName]: row.original[selectFieldName],
    });
    setErrors({ [entityFieldName]: false, [selectFieldName]: false });
  };

  const table: any = useMaterialReactTable({
    columns,
    data,
    manualPagination: false,
    manualSorting: false,
    manualFiltering: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableClickToCopy: true,
    enableKeyboardShortcuts: true,
    enableColumnActions: true,
    enableColumnDragging: true,
    enableGlobalFilter: false,
    enableGrouping: true,
    enableHiding: true,
    enableSorting: true,
    enableColumnPinning: true,
    enableRowSelection: true,
    enableColumnResizing: true,
    enableFilterMatchHighlighting: true,
    enableDensityToggle: false,
    createDisplayMode: "row",
    editDisplayMode: "row",
    enableEditing: true,
    layoutMode: "grid",
    onRowSelectionChange: setRowSelection,
    state: { rowSelection },
    muiTopToolbarProps: () => ({ sx: { backgroundColor: "#fff", padding: "8px" }, className: "top-toolbar-wrapper__new" }),
    muiTableProps: { sx: { tableLayout: "auto", width: "100%" } },
    muiTableContainerProps: () => ({ sx: { backgroundColor: "#fff" }, className: "table-container-wrapper__new" }),
    muiTableFooterProps: () => ({ sx: { backgroundColor: "#fff" }, className: "table-footer-wrapper__new" }),
    muiBottomToolbarProps: () => ({ sx: { backgroundColor: "#fff", padding: "8px" }, className: "bottom-toolbar-wrapper__new" }),
    getRowId: (row: any) => row?.id,
    displayColumnDefOptions: {
      "mrt-row-actions": { size: 120, minSize: 60, maxSize: 120, grow: false },
    },
    renderToolbarInternalActions: (props) => <CommonToolbarInternalActions {...props} />,
    renderTopToolbarCustomActions: () => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        {fileUploadApiUrl && (
          <Button startIcon={<UploadFileIcon />} onClick={() => setOpenFileUpload(true)}>
            Upload File
          </Button>
        )}
        {exportAllApiUrl && (
          <Button startIcon={<BrowserUpdatedIcon />} onClick={handleDataDownload}>
            Export All Data
          </Button>
        )}
        {templateDownloadApiUrl && (
          <BrokerTemplateDownloadButton
            apiEndpoint={templateDownloadApiUrl}
            buttonText="Download Template"
            fileName={templateFileName}
          />
        )}
        {Object.keys(rowSelection).length > 0 && (
      <Button
        onClick={handleExportSelectedRows}
        style={{ fontSize: "small", textTransform: "none" }}
        startIcon={<FileDownloadIcon style={{ fontSize: "small" }} />}
      >
        Export Selected ({Object.keys(rowSelection).length})
      </Button>
    )}
        {Object.keys(rowSelection).length > 0 && (
          <Button
            onClick={handleBulkDelete}
            style={{ color: "red", fontSize: "small", textTransform: "none" }}
            startIcon={<DeleteIcon style={{ fontSize: "small" }} />}
          >
            Delete Selected ({Object.keys(rowSelection).length})
          </Button>
        )}
      </Box>
    ),
    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", gap: "0.5rem" }}>
        <Tooltip title="Edit">
          <IconButton onClick={() => fillFormForEdit(row)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete" className="deleteIcon">
          <IconButton color="error" onClick={() => handleDelete(row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copy">
          <IconButton onClick={() => handleCopyRow(row)}>
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  });

  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <div>
            <div className="table-ad">
              <Accordion defaultActiveKey="0" className="table-ad__addInputs">
                <Accordion.Item eventKey="0">
                  <Accordion.Header className="accordion_button">{pageTitle}</Accordion.Header>
                  <Accordion.Body>
                    <div className="form_contain">
                      <div
                        className={`flex formWrapper ${inputBackgroundColour !== "" ? "inputboxcustom" : ""}`}
                        style={{ backgroundColor: inputBackgroundColour }}
                      >
                        <Box sx={{ width: "100%", p: 1, pt: "20px !important", backgroundColor: "#ffff", borderRadius: "4px", border: "1px solid #e0e0e0" }}>
                          <Box className="form_default" sx={{ display: "flex", flexWrap: "nowrap", alignItems: "center", justifyContent: "flex-start", gap: 1 }}>
                            <TextField
                              size="small"
                              label={entityLabel}
                              value={formData[entityFieldName]}
                              InputLabelProps={{ shrink: true }}
                              onChange={(e) => {
                                setFormData({ ...formData, [entityFieldName]: e.target.value });
                                if (errors[entityFieldName]) {
                                  setErrors((prev) => ({ ...prev, [entityFieldName]: false }));
                                }
                              }}
                              error={errors[entityFieldName]}
                              helperText={errors[entityFieldName] ? `${entityLabel} is required` : ""}
                              sx={compactInputStyle}
                            />

                            <TextField
                              size="small"
                              select
                              label={selectLabel}
                              value={formData[selectFieldName]}
                              InputLabelProps={{ shrink: true }}
                              onChange={(e) => {
                                setFormData({ ...formData, [selectFieldName]: e.target.value });
                                if (errors[selectFieldName]) {
                                  setErrors((prev) => ({ ...prev, [selectFieldName]: false }));
                                }
                              }}
                              error={errors[selectFieldName]}
                              helperText={errors[selectFieldName] ? `${selectLabel} is required` : ""}
                              sx={compactInputStyle}
                            >
                              {selectOptions.map((opt: any) => (
                                <MenuItem key={opt.id} value={opt.id} style={{ fontSize: "0.75rem" }}>
                                  {opt.name}
                                </MenuItem>
                              ))}
                            </TextField>

                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button
                                onClick={editId ? handleUpdate : handleCreate}
                                variant="contained"
                                disabled={isUnchangedInEditMode}
                                sx={{ height: "32px", minWidth: "100px", backgroundColor: "#00bcd4", textTransform: "none", fontSize: "0.75rem" }}
                              >
                                {editId ? "Update" : "Add"}
                              </Button>

                              <Button
                                onClick={handleClear}
                                variant="outlined"
                                sx={{ height: "32px", minWidth: "80px", textTransform: "none", fontSize: "0.75rem" }}
                              >
                                Clear
                              </Button>

                              {editId && (
                                <Button
                                  onClick={handleCancelEdit}
                                  variant="outlined"
                                  color="error"
                                  sx={{ height: "32px", minWidth: "80px", textTransform: "none", fontSize: "0.75rem" }}
                                >
                                  Cancel
                                </Button>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>

              <div className="table-ad__wrapper">
                <div className="table-ad__wrapperCard">
                  <MaterialReactTable table={table} />
                </div>
                {fileUploadApiUrl && (
                  <FileUpload
                    open={openFileUpload}
                    onFileUploadClose={() => setOpenFileUpload(false)}
                    onFileUploadSubmit={handleFileUploadSubmit}
                    acceptedFileTypes=".csv"
                  />
                )}
              </div>
            </div>
          </div>
        </Row>
      </Container>
    </div>
  );
};

export default GenericMappingTable;